import { cn } from '@/lib/utils';
import { useState, useEffect, type HTMLAttributes } from 'react';

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-md',
        'bg-gradient-to-r from-muted via-muted/50 to-muted',
        'bg-[length:200%_100%]',
        'animate-shimmer',
        className
      )}
      {...props}
    />
  );
}

export const PhotoCardSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <Skeleton data-testid="photo-skeleton" className={cn('aspect-square rounded-lg', className)} />
  );
};

interface PhotoGridSkeletonProps {
  count?: number;
  className?: string;
  'aria-label'?: string;
}

export const PhotoGridSkeleton: React.FC<PhotoGridSkeletonProps> = ({
  count = 12,
  className,
  'aria-label': ariaLabel,
}) => {
  return (
    <div
      className={cn(
        'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4',
        className
      )}
      aria-label={ariaLabel}
      role={ariaLabel ? 'region' : undefined}
    >
      {Array.from({ length: count }).map((_, i) => (
        <PhotoCardSkeleton key={i} />
      ))}
    </div>
  );
};

export const TextSkeleton: React.FC<{
  width?: string;
  className?: string;
}> = ({ width = 'w-full', className }) => {
  return <Skeleton className={cn('h-4', width, className)} />;
};

export const AvatarSkeleton: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sizes = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  return <Skeleton className={cn('rounded-full', sizes[size])} />;
};

/**
 * SkeletonLoader - Wrapper component that handles transition from skeleton to content
 *
 * Features:
 * - Shows skeleton while isLoading is true
 * - Fades out skeleton and fades in content when loading completes
 * - Smooth crossfade transition animation
 */
interface SkeletonLoaderProps {
  /** Whether content is still loading */
  isLoading: boolean;
  /** Skeleton component to show while loading */
  skeleton: React.ReactNode;
  /** Content to show when loaded */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Transition duration in ms (default: 200) */
  transitionDuration?: number;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  isLoading,
  skeleton,
  children,
  className,
  transitionDuration = 200,
}) => {
  // Track transition state for fade-out effect
  const [showSkeleton, setShowSkeleton] = useState(isLoading);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (isLoading) {
      // Loading started - show skeleton immediately
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowSkeleton(true);
      setIsTransitioning(false);
    } else if (showSkeleton) {
      // Loading finished - start fade-out transition
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setShowSkeleton(false);
        setIsTransitioning(false);
      }, transitionDuration);
      return () => clearTimeout(timer);
    }
  }, [isLoading, showSkeleton, transitionDuration]);

  // During transition: show both with crossfade
  if (isTransitioning) {
    return (
      <div className={cn('relative', className)} data-testid="skeleton-loader-transitioning">
        {/* Skeleton fading out */}
        <div
          className="animate-fade-out absolute inset-0"
          style={{ animationDuration: `${transitionDuration}ms` }}
        >
          {skeleton}
        </div>
        {/* Content fading in */}
        <div className="animate-fade-in" style={{ animationDuration: `${transitionDuration}ms` }}>
          {children}
        </div>
      </div>
    );
  }

  // Show skeleton while loading
  if (showSkeleton) {
    return (
      <div className={cn('animate-fade-in', className)} data-testid="skeleton-loader-loading">
        {skeleton}
      </div>
    );
  }

  // Show content when loaded
  return (
    <div className={cn('animate-fade-in', className)} data-testid="skeleton-loader-content">
      {children}
    </div>
  );
};
